import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import { FileText, Loader2, Sparkles, UserRound } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import ProfileChoice from './ProfileChoice';
import ProfileForm from './ProfileForm';
import ProfileSkillsReviewSection from './ProfileSkillsReviewSection';
import RoleRecommendations from './RoleRecommendations';
import ResumeUpload from './ResumeUpload';
import { profileAPI } from '../../services/api';

const cleanText = (value) => (typeof value === 'string' ? value.trim() : '');

const uniqueBy = (items, getKey) => {
  const seen = new Set();
  return items.filter((item) => {
    const key = getKey(item);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const normalizeSkillsForForm = (skills) => {
  const list = Array.isArray(skills) ? skills : skills ? [skills] : [];
  const mapped = list
    .map((skill) => (typeof skill === 'string' ? skill : skill?.name || skill?.skill || skill?.title || ''))
    .map(cleanText)
    .filter(Boolean)
    .map((name) => ({ name }));
  return uniqueBy(mapped, (skill) => skill.name.toLowerCase());
};

const normalizeEducationForForm = (education) => {
  const list = Array.isArray(education) ? education : education ? [education] : [];
  const mapped = list
    .filter((entry) => entry && typeof entry === 'object')
    .map((entry) => ({
      institution: cleanText(entry.institution || entry.university || entry.school),
      degree: cleanText(entry.degree),
      field_of_study: cleanText(entry.field_of_study || entry.field || entry.major),
      start_year: cleanText(entry.start_year),
      end_year: cleanText(entry.end_year),
    }))
    .filter((entry) => entry.institution || entry.degree);
  return uniqueBy(mapped, (entry) => `${entry.institution.toLowerCase()}|${entry.degree.toLowerCase()}`);
};

const normalizeProjectsForForm = (projects) => {
  const list = Array.isArray(projects) ? projects : projects ? [projects] : [];
  const mapped = list
    .filter((project) => project && typeof project === 'object')
    .map((project) => ({
      title: cleanText(project.title),
      description: cleanText(project.description),
    }))
    .filter((project) => project.title || project.description);
  return uniqueBy(mapped, (project) => `${project.title.toLowerCase()}|${project.description.toLowerCase()}`);
};

const normalizeProfile = (profile) => ({
  name: cleanText(profile?.name),
  email: cleanText(profile?.email),
  phone: cleanText(profile?.phone),
  skills: normalizeSkillsForForm(profile?.skills),
  education: normalizeEducationForForm(profile?.education),
  projects: normalizeProjectsForForm(profile?.projects),
});

const hasAutofillData = (profile) => {
  const normalized = normalizeProfile(profile);
  return Boolean(
    normalized.name ||
      normalized.email ||
      normalized.phone ||
      normalized.skills.length ||
      normalized.education.length ||
      normalized.projects.length
  );
};

const ProfileSetupContent = ({
  onProceed,
  onSummaryChange,
  proceedLabel = 'Continue',
  isEmbedded = false,
}) => {
  const { updateUser } = useAuth();
  const [mode, setMode] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSkillsLoading, setIsSkillsLoading] = useState(false);
  const [isRecommendationsLoading, setIsRecommendationsLoading] = useState(false);
  const [isSelectingRole, setIsSelectingRole] = useState(false);
  const [profileSkills, setProfileSkills] = useState([]);
  const [reviewMessage, setReviewMessage] = useState('');
  const [recommendedRoles, setRecommendedRoles] = useState([]);
  const [sourceSkills, setSourceSkills] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [customRole, setCustomRole] = useState('');
  const [finalRoleTitle, setFinalRoleTitle] = useState('');
  const [finalRoleType, setFinalRoleType] = useState(null);
  const [customRoleFramework, setCustomRoleFramework] = useState(null);

  const buildSummary = useCallback(() => ({
    profile,
    profileSkills,
    sourceSkills,
    selectedRoleId,
    customRole,
    finalRoleTitle,
    finalRoleType,
    hasFinalRole: finalRoleType === 'recommended' || finalRoleType === 'custom',
  }), [customRole, finalRoleTitle, finalRoleType, profile, profileSkills, selectedRoleId, sourceSkills]);

  const applyRoleState = useCallback((response) => {
    const nextFinalRoleType = response?.final_role_type || null;
    const hasFinalRole = nextFinalRoleType === 'recommended' || nextFinalRoleType === 'custom';

    setSelectedRoleId(response?.selected_role_id || null);
    setCustomRole(response?.custom_role || '');
    setFinalRoleType(nextFinalRoleType);
    setFinalRoleTitle(hasFinalRole ? response?.selected_role_title || response?.final_role_title || '' : '');
    setCustomRoleFramework(response?.custom_role_framework || null);
    if (Array.isArray(response?.source_skills)) {
      setSourceSkills(response.source_skills);
    }
  }, []);

  const loadRecommendations = useCallback(async () => {
    try {
      setIsRecommendationsLoading(true);
      const response = await profileAPI.getRoleRecommendations();
      setRecommendedRoles(response.recommended_roles || []);
      applyRoleState(response);
    } catch (error) {
      toast.error(error.message || 'Unable to load role recommendations');
    } finally {
      setIsRecommendationsLoading(false);
    }
  }, [applyRoleState]);

  const loadProfileSkills = useCallback(async () => {
    try {
      setIsSkillsLoading(true);
      const skills = await profileAPI.getProfileSkills();
      setProfileSkills(Array.isArray(skills) ? skills : []);
    } catch (error) {
      if (!error.message?.toLowerCase().includes('not found')) {
        toast.error(error.message || 'Unable to load reviewed skills');
      }
    } finally {
      setIsSkillsLoading(false);
    }
  }, []);

  const refreshRoleRecommendations = useCallback(async () => {
    await loadProfileSkills();
    await loadRecommendations();
  }, [loadProfileSkills, loadRecommendations]);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const existing = await profileAPI.getProfile();
        setProfile(existing);
        setMode('manual');
        await refreshRoleRecommendations();
      } catch (error) {
        if (!error.message?.toLowerCase().includes('not found')) {
          toast.error(error.message || 'Unable to load profile');
        }
      } finally {
        setIsLoading(false);
      }
    };
    loadProfile();
  }, [refreshRoleRecommendations]);

  useEffect(() => {
    onSummaryChange?.(buildSummary());
  }, [buildSummary, onSummaryChange]);

  const saveProfile = async (payload) => {
    try {
      setIsSaving(true);
      const saved = profile ? await profileAPI.updateProfile(payload) : await profileAPI.createProfile(payload);
      setProfile(saved);
      await refreshRoleRecommendations();
      toast.success('Profile saved successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to save profile');
    } finally {
      setIsSaving(false);
    }
  };

  const uploadResume = async (file) => {
    try {
      setIsSaving(true);
      const response = await profileAPI.uploadResume(file);
      setProfile(response.profile);
      setMode('manual');
      await refreshRoleRecommendations();
      if (response.warning) {
        setReviewMessage(response.warning);
        toast(response.warning);
      } else if (hasAutofillData(response.profile)) {
        setReviewMessage('Resume parsed successfully. Review and edit the autofilled fields before saving.');
        toast.success(response.message || 'Resume parsed successfully');
      } else {
        setReviewMessage('Resume uploaded, but we could not confidently parse profile fields. You can fill them in manually.');
        toast('Resume uploaded. Add missing profile details manually.');
      }
    } catch (error) {
      setReviewMessage('Resume parsing failed. You can still create your profile manually.');
      setMode('manual');
      toast.error(error.message || 'Failed to upload resume');
    } finally {
      setIsSaving(false);
    }
  };

  const addReviewedSkill = async (skillName) => {
    try {
      setIsSaving(true);
      const skill = await profileAPI.addProfileSkill(skillName);
      setProfileSkills((current) => {
        const exists = current.some((item) => item.skill_name.toLowerCase() === skill.skill_name.toLowerCase());
        return exists ? current : [...current, skill];
      });
      await loadRecommendations();
      toast.success('Skill added');
    } catch (error) {
      toast.error(error.message || 'Failed to add skill');
    } finally {
      setIsSaving(false);
    }
  };

  const removeReviewedSkill = async (skill) => {
    try {
      setIsSaving(true);
      if (skill.id) {
        await profileAPI.deleteProfileSkill(skill.id);
      } else {
        await profileAPI.updateProfileSkills(profileSkills.filter((item) => item.skill_name !== skill.skill_name));
      }
      setProfileSkills((current) => current.filter((item) => item.id !== skill.id && item.skill_name !== skill.skill_name));
      await loadRecommendations();
      toast.success('Skill removed');
    } catch (error) {
      toast.error(error.message || 'Failed to remove skill');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSelectRole = async (roleId) => {
    try {
      setIsSelectingRole(true);
      const response = await profileAPI.selectRecommendedRole(roleId);
      applyRoleState(response);
      updateUser?.({ selected_role: response.selected_role_id, career_goal: response.active_career_goal });
      toast.success(response.message || 'Role selected successfully');
      await loadRecommendations();
    } catch (error) {
      toast.error(error.message || 'Failed to save selected role');
    } finally {
      setIsSelectingRole(false);
    }
  };

  const handleSelectCustomRole = async (roleName) => {
    try {
      setIsSelectingRole(true);
      const response = await profileAPI.selectCustomRole(roleName);
      applyRoleState(response);
      updateUser?.({ selected_role: null, career_goal: response.active_career_goal });
      toast.success(response.message || 'Custom role saved successfully');
      await loadRecommendations();
    } catch (error) {
      toast.error(error.message || 'Failed to save custom role');
    } finally {
      setIsSelectingRole(false);
    }
  };

  const handleDeselectRole = async () => {
    try {
      setIsSelectingRole(true);
      const response = await profileAPI.deselectRecommendedRole();
      applyRoleState(response);
      updateUser?.({ selected_role: null, career_goal: response.active_career_goal });
      toast.success(response.message || 'Role selection cleared');
      await loadRecommendations();
    } catch (error) {
      toast.error(error.message || 'Failed to clear selected role');
    } finally {
      setIsSelectingRole(false);
    }
  };

  const handleProceed = () => {
    onProceed?.(buildSummary());
  };

  if (isLoading) {
    return (
      <div className={isEmbedded ? 'px-2 text-white' : 'flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 px-6 text-white'}>
        <div className="w-full max-w-3xl rounded-2xl border border-slate-700/50 bg-slate-800/50 p-6 shadow-2xl shadow-purple-950/40 backdrop-blur-xl">
          <div className="mb-5 h-3 w-28 animate-pulse rounded-full bg-purple-400/40" />
          <div className="mb-3 h-9 w-3/4 animate-pulse rounded-2xl bg-white/10" />
          <div className="h-4 w-1/2 animate-pulse rounded-full bg-white/10" />
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            <div className="h-44 animate-pulse rounded-2xl bg-white/10" />
            <div className="h-44 animate-pulse rounded-2xl bg-white/10" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={isEmbedded ? 'text-white' : 'relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 px-4 pb-16 pt-20 sm:px-6'}>
      {!isEmbedded && (
        <>
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_12%,rgba(168,85,247,0.22),transparent_34%),radial-gradient(circle_at_80%_8%,rgba(236,72,153,0.18),transparent_30%),radial-gradient(circle_at_50%_100%,rgba(126,34,206,0.18),transparent_35%)]" />
          <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-purple-300/70 to-transparent" />
          <div className="pointer-events-none absolute -right-24 top-24 h-72 w-72 rounded-full bg-pink-500/10 blur-3xl" />
          <div className="pointer-events-none absolute -left-28 bottom-24 h-80 w-80 rounded-full bg-purple-500/10 blur-3xl" />
        </>
      )}

      <div className={isEmbedded ? 'mx-auto max-w-6xl' : 'relative mx-auto max-w-6xl'}>
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="relative mb-10 overflow-hidden rounded-2xl border border-slate-700/50 bg-slate-800/50 p-6 shadow-2xl shadow-purple-950/40 backdrop-blur-xl sm:p-8"
        >
          <div className="absolute inset-x-0 top-0 h-48 bg-gradient-to-br from-purple-600/20 to-pink-600/20" />
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="relative inline-flex items-center gap-2 rounded-full border border-purple-400/30 bg-purple-500/20 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-purple-200">
                <Sparkles className="h-3.5 w-3.5" />
                Career Profile
              </div>
              <h1 className="relative mt-5 max-w-3xl text-4xl font-bold leading-tight text-white sm:text-5xl">
                Build your AI-ready learning identity
              </h1>
              <p className="relative mt-4 max-w-2xl text-lg leading-8 text-gray-300">
                Create a structured profile or upload your resume so AIVION Learn can personalize roadmap, project, and career guidance around your goals.
              </p>
            </div>
            <div className="relative grid grid-cols-2 gap-3 text-sm text-gray-300 sm:min-w-72">
              <div className="rounded-2xl border border-slate-700/50 bg-slate-900/50 p-4 backdrop-blur-lg">
                <p className="text-2xl font-bold text-white">{profile?.skills?.length || 0}</p>
                <p className="mt-1">Skills mapped</p>
              </div>
              <div className="rounded-2xl border border-slate-700/50 bg-slate-900/50 p-4 backdrop-blur-lg">
                <p className="text-2xl font-bold text-purple-300">{profile ? 'Live' : 'New'}</p>
                <p className="mt-1">Profile status</p>
              </div>
            </div>
          </div>
        </motion.div>

        {!mode && <ProfileChoice onSelect={setMode} />}
        {mode && (
          <div className="mb-6 flex flex-col gap-3 rounded-2xl border border-slate-700/50 bg-slate-800/50 p-2 backdrop-blur-xl sm:inline-flex sm:flex-row">
            <button
              type="button"
              onClick={() => {
                setReviewMessage('');
                setMode('manual');
              }}
              className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold transition ${mode === 'manual' ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-500/25' : 'text-gray-300 hover:bg-purple-500/20 hover:text-white'}`}
            >
              <UserRound className="h-4 w-4" />
              Create Profile
            </button>
            <button
              type="button"
              onClick={() => {
                setReviewMessage('');
                setMode('resume');
              }}
              className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold transition ${mode === 'resume' ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-500/25' : 'text-gray-300 hover:bg-purple-500/20 hover:text-white'}`}
            >
              <FileText className="h-4 w-4" />
              Upload Resume
            </button>
          </div>
        )}
        {mode === 'manual' && (
          <>
            <ProfileForm initialValue={normalizeProfile(profile)} onSubmit={saveProfile} isSaving={isSaving} reviewMessage={reviewMessage} />
            {(profile || profileSkills.length > 0) && (
              <>
                <div className="mt-5">
                  <ProfileSkillsReviewSection
                    skills={profileSkills}
                    isLoading={isSkillsLoading}
                    isSaving={isSaving}
                    onAddSkill={addReviewedSkill}
                    onRemoveSkill={removeReviewedSkill}
                    onRefresh={refreshRoleRecommendations}
                  />
                </div>
                <div className="mt-5">
                  <RoleRecommendations
                    recommendations={recommendedRoles}
                    sourceSkills={sourceSkills}
                    selectedRoleId={selectedRoleId}
                    customRole={customRole}
                    finalRoleTitle={finalRoleTitle}
                    finalRoleType={finalRoleType}
                    customRoleFramework={customRoleFramework}
                    onSelectRole={handleSelectRole}
                    onSelectCustomRole={handleSelectCustomRole}
                    onDeselectRole={handleDeselectRole}
                    onProceed={handleProceed}
                    isLoading={isRecommendationsLoading}
                    isSelectingRole={isSelectingRole}
                    proceedLabel={proceedLabel}
                  />
                </div>
              </>
            )}
          </>
        )}
        {mode === 'resume' && <ResumeUpload onUpload={uploadResume} isUploading={isSaving} />}
        {isRecommendationsLoading && (
          <div className="fixed bottom-5 left-5 z-30 hidden items-center gap-3 rounded-2xl border border-purple-400/30 bg-slate-950/90 px-4 py-3 text-sm font-semibold text-purple-100 shadow-2xl shadow-purple-950/40 backdrop-blur-xl sm:flex">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading role recommendations
          </div>
        )}
        {isSaving && (
          <div className="fixed bottom-5 right-5 z-30 hidden items-center gap-3 rounded-2xl border border-purple-400/30 bg-slate-950/90 px-4 py-3 text-sm font-semibold text-purple-100 shadow-2xl shadow-purple-950/40 backdrop-blur-xl sm:flex">
            <Loader2 className="h-4 w-4 animate-spin" />
            Parsing resume and filling profile
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileSetupContent;
